import { useEffect, useRef, useCallback } from 'react'

const SHAPES = {
    blue: {
        color: '#4E9ED6',
        lobes: [
            { cx: 449, cy: 205, r: 125 },
            { cx: 680, cy: 445, r: 125 },
            { cx: 915, cy: 205, r: 125 },
        ],
    },
    purple: {
        color: '#904C97',
        lobes: [
            { cx: 1140, cy: 430, r: 120 },
            { cx: 920, cy: 680, r: 120 },
            { cx: 1140, cy: 930, r: 120 },
        ],
    },
    pink: {
        color: '#E9408C',
        lobes: [
            { cx: 940, cy: 1140, r: 115 },
            { cx: 695, cy: 900, r: 115 },
            { cx: 455, cy: 1160, r: 115 },
        ],
    },
    yellow: {
        color: '#F8A515',
        lobes: [
            { cx: 235, cy: 920, r: 115 },
            { cx: 440, cy: 670, r: 115 },
            { cx: 235, cy: 440, r: 115 },
        ],
    },
}

const SHAPE_ORDER = ['blue', 'purple', 'pink', 'yellow']
// Velocidad del splash de entrada. Es el default: quien lo use para otra cosa la pasa
// por prop. El builder lo muestra mientras el modelo trabaja y ahí va más rápido, porque
// el ciclo entero no es la bienvenida sino la señal de que algo está pasando ahora.
const SPEED_DEFAULT = 1.8

function bridgeCircles(a, b, count) {
    const bridges = []
    for (let i = 1; i <= count; i++) {
        const t = i / (count + 1)
        bridges.push({
            cx: a.cx + (b.cx - a.cx) * t,
            cy: a.cy + (b.cy - a.cy) * t,
            r: Math.min(a.r, b.r) * 0.45,
        })
    }
    return bridges
}

function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3) }
function easeInCubic(t) { return t * t * t }
function lerp(a, b, t) { return a + (b - a) * t }

function animateFrame(duration, onTick) {
    return new Promise(resolve => {
        const t0 = performance.now()
        ;(function step(now) {
            const t = Math.min((now - t0) / duration, 1)
            onTick(t)
            if (t < 1) requestAnimationFrame(step)
            else resolve()
        })(performance.now())
    })
}

function sleep(ms) { return new Promise(r => setTimeout(r, ms)) }

const LogoSplash = ({ size = 140, speed = SPEED_DEFAULT }) => {
    const svgRef = useRef(null)
    const cancelledRef = useRef(false)
    const SPEED = speed

    const runAnimation = useCallback(async () => {
        const svg = svgRef.current
        if (!svg) return

        const groups = {}
        SHAPE_ORDER.forEach(key => {
            const def = SHAPES[key]
            const group = svg.querySelector(`#group-${key}`)
            if (!group) return

            const lobes = def.lobes
            const bridge01 = bridgeCircles(lobes[0], lobes[1], 3)
            const bridge12 = bridgeCircles(lobes[1], lobes[2], 3)
            const allCircles = [lobes[0], ...bridge01, lobes[1], ...bridge12, lobes[2]]

            // Clear existing circles
            while (group.firstChild) group.removeChild(group.firstChild)

            const els = allCircles.map(() => {
                const el = document.createElementNS('http://www.w3.org/2000/svg', 'circle')
                el.setAttribute('cx', String(lobes[0].cx))
                el.setAttribute('cy', String(lobes[0].cy))
                el.setAttribute('r', '0')
                el.setAttribute('fill', def.color)
                group.appendChild(el)
                return el
            })

            groups[key] = { els, allCircles, lobes }
        })

        async function buildShape(key) {
            const { els, allCircles, lobes } = groups[key]
            const group = svg.querySelector(`#group-${key}`)

            els.forEach(el => {
                el.setAttribute('cx', String(lobes[0].cx))
                el.setAttribute('cy', String(lobes[0].cy))
                el.setAttribute('r', '0')
            })
            group.setAttribute('opacity', '1')

            await animateFrame(350 / SPEED, t => {
                els[0].setAttribute('r', String(allCircles[0].r * easeOutCubic(t)))
            })

            await animateFrame(500 / SPEED, t => {
                const e = easeOutCubic(t)
                for (let i = 1; i <= 4; i++) {
                    const target = allCircles[i]
                    els[i].setAttribute('cx', String(lerp(lobes[0].cx, target.cx, e)))
                    els[i].setAttribute('cy', String(lerp(lobes[0].cy, target.cy, e)))
                    const delay = i < 4 ? 0 : 0.15
                    const adjustedT = Math.max(0, (t - delay) / (1 - delay))
                    els[i].setAttribute('r', String(target.r * easeOutCubic(adjustedT)))
                }
            })

            await animateFrame(500 / SPEED, t => {
                const e = easeOutCubic(t)
                for (let i = 5; i <= 8; i++) {
                    const target = allCircles[i]
                    els[i].setAttribute('cx', String(lerp(lobes[1].cx, target.cx, e)))
                    els[i].setAttribute('cy', String(lerp(lobes[1].cy, target.cy, e)))
                    const delay = i < 8 ? 0 : 0.15
                    const adjustedT = Math.max(0, (t - delay) / (1 - delay))
                    els[i].setAttribute('r', String(target.r * easeOutCubic(adjustedT)))
                }
            })
        }

        async function fadeOutAll() {
            const allGroups = SHAPE_ORDER.map(s => svg.querySelector(`#group-${s}`))
            await animateFrame(400 / SPEED, t => {
                allGroups.forEach(g => g?.setAttribute('opacity', String(1 - easeInCubic(t))))
            })
            allGroups.forEach(g => g?.setAttribute('opacity', '0'))
        }

        while (!cancelledRef.current) {
            await sleep(350 / SPEED)
            if (cancelledRef.current) break

            for (const key of SHAPE_ORDER) {
                if (cancelledRef.current) break
                await buildShape(key)
                await sleep(100 / SPEED)
            }

            if (cancelledRef.current) break
            await sleep(1000 / SPEED)
            await fadeOutAll()
            await sleep(400 / SPEED)
        }
    }, [SPEED])

    useEffect(() => {
        cancelledRef.current = false
        runAnimation()
        return () => { cancelledRef.current = true }
    }, [runAnimation])

    return (
        <div style={{ width: size, height: size }}>
            <svg
                ref={svgRef}
                width="100%"
                height="100%"
                viewBox="0 0 1365 1365"
                fill="none"
            >
                <defs>
                    <filter id="goo" x="-30%" y="-30%" width="160%" height="160%">
                        <feGaussianBlur in="SourceGraphic" stdDeviation="30" result="blur" />
                        <feColorMatrix
                            in="blur"
                            mode="matrix"
                            values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 38 -15"
                            result="goo"
                        />
                    </filter>
                </defs>
                <g id="group-blue" filter="url(#goo)" opacity="0" />
                <g id="group-purple" filter="url(#goo)" opacity="0" />
                <g id="group-pink" filter="url(#goo)" opacity="0" />
                <g id="group-yellow" filter="url(#goo)" opacity="0" />
            </svg>
        </div>
    )
}

export default LogoSplash
